import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Chip,
  Divider,
  Collapse,
  IconButton
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { formatPrice } from './bookingService';

interface Service {
  id: string;
  name: string;
  price: number | null;
  description: string | null;
  duration_in_minutes: number;
}

interface ServiceSelectionProps {
  services: Service[];
  selectedService: Service | null;
  onServiceSelect: (service: Service) => void;
}

const ServiceSelection: React.FC<ServiceSelectionProps> = ({ 
  services, 
  selectedService, 
  onServiceSelect 
}) => {
  // Track which service descriptions are expanded
  const [expandedServices, setExpandedServices] = useState<Record<string, boolean>>({});
  
  // Toggle description expansion for a specific service
  const toggleDescription = (serviceId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent the card from being selected when clicking the toggle button
    setExpandedServices(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
  };
  
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select a Service
      </Typography>
      
      {services.length === 0 ? (
        <Typography color="text.secondary">
          No services available for this clinic
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {services.map((service) => (
            <Grid item xs={12} key={service.id}>
              <Card 
                variant="outlined"
                sx={{
                  borderColor: selectedService?.id === service.id ? 'primary.main' : 'divider',
                  borderWidth: selectedService?.id === service.id ? 2 : 1,
                }}
              >
                <CardActionArea onClick={() => onServiceSelect(service)}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="h6" component="div">
                        {service.name}
                      </Typography>
                      <Chip
                        label={formatPrice(service.price)}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography color="text.secondary" variant="body2">
                        Duration: {service.duration_in_minutes} minutes
                      </Typography>
                      
                      {service.description && (
                        <IconButton 
                          size="small" 
                          onClick={(e) => toggleDescription(service.id, e)}
                          sx={{ ml: 1 }}
                        >
                          {expandedServices[service.id] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      )}
                    </Box>
                    
                    {service.description && (
                      <Collapse in={expandedServices[service.id]} timeout="auto" unmountOnExit>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body2" sx={{ mt: 1, pb: 1 }}>
                          {service.description}
                        </Typography>
                      </Collapse>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default ServiceSelection;